import * as React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, Users, Phone, FileText, Euro, CheckCircle2, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { formatBerlinDate, formatBerlinTime } from '@/lib/berlin-time';

interface ActivityReport {
  kassenpatienten: number;
  privatpatienten: number;
  telefonischeBeratungen: number;
  totenscheine: number;
  submittedAt: number;
}

interface ActivityReportSectionProps {
  activityReport: ActivityReport;
  hasNotfallscheinbeteiligung?: boolean;
  notfallscheinbeteiligungRate?: number;
  isConfirmed?: boolean;
  confirmedAt?: number;
  className?: string;
}

/**
 * ActivityReportSection - Display component for substitute's activity report
 *
 * Design: Professional medical aesthetic with clear data hierarchy
 * - Header with clipboard icon and timestamp
 * - 2x2 stat grid with large numbers and descriptive labels
 * - Total deployments summary
 * - Notfallscheinbeteiligung calculation highlight (if applicable)
 * - Confirmation status badge
 */
export const ActivityReportSection: React.FC<ActivityReportSectionProps> = ({ activityReport, hasNotfallscheinbeteiligung, notfallscheinbeteiligungRate, isConfirmed, confirmedAt, className }) => {
  const { t } = useTranslation(['duties']);

  const fmt = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });

  // Format timestamps
  const formattedSubmitDate = formatBerlinDate(activityReport.submittedAt);
  const formattedSubmitTime = formatBerlinTime(activityReport.submittedAt);
  const formattedConfirmDate = confirmedAt ? formatBerlinDate(confirmedAt) : null;

  // Calculate totals
  const totalDeployments = activityReport.kassenpatienten + activityReport.privatpatienten + activityReport.telefonischeBeratungen + activityReport.totenscheine;

  const scheinbeteiligungTotal = hasNotfallscheinbeteiligung && notfallscheinbeteiligungRate ? activityReport.kassenpatienten * notfallscheinbeteiligungRate : 0;

  // Stat items configuration
  const statItems = [
    {
      label: t('duties:activityReport.kassenpatienten'),
      hint: t('duties:activityReport.kassenpatientenHint'),
      value: activityReport.kassenpatienten,
      icon: Users,
      highlight: hasNotfallscheinbeteiligung,
    },
    {
      label: t('duties:activityReport.privatpatienten'),
      hint: t('duties:activityReport.privatpatientenHint'),
      value: activityReport.privatpatienten,
      icon: Users,
      highlight: false,
    },
    {
      label: t('duties:activityReport.telefonischeBeratungen'),
      hint: t('duties:activityReport.telefonischeBeratungenHint'),
      value: activityReport.telefonischeBeratungen,
      icon: Phone,
      highlight: false,
    },
    {
      label: t('duties:activityReport.totenscheine'),
      hint: t('duties:activityReport.totenscheineHint'),
      value: activityReport.totenscheine,
      icon: FileText,
      highlight: false,
    },
  ];

  return (
    <Card className={cn('overflow-hidden shadow-sm', className)}>
      {/* Header */}
      <div className='p-4 lg:p-6 pb-0'>
        <div className='flex items-start justify-between gap-3'>
          <div className='flex items-start gap-3'>
            <div className='flex items-center justify-center size-10 rounded-xl bg-primary/10 shrink-0'>
              <ClipboardList className='size-5 text-primary' />
            </div>
            <div className='flex-1 min-w-0'>
              <div className='flex items-center gap-2 flex-wrap'>
                <h3 className='text-base font-semibold text-foreground'>{t('duties:activityReport.title')}</h3>
                {isConfirmed && (
                  <Badge variant='outline' className='bg-success/10 text-success border-success/30 gap-1'>
                    <CheckCircle2 className='size-3' />
                    {t('duties:detail.confirmed')}
                  </Badge>
                )}
              </div>
              <div className='flex items-center gap-1.5 mt-1 text-sm text-muted-foreground'>
                <Clock className='size-3.5' />
                <span>{t('duties:activityReport.submittedAt', { date: `${formattedSubmitDate} um ${formattedSubmitTime}` })}</span>
              </div>
              {isConfirmed && formattedConfirmDate && (
                <div className='flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground'>
                  <CheckCircle2 className='size-3' />
                  <span>Bestätigt am {formattedConfirmDate}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className='p-4 lg:p-6 space-y-4'>
        {/* 2x2 Stats Grid */}
        <div className='grid grid-cols-2 gap-3'>
          {statItems.map((item) => (
            <div key={item.label} className={cn('rounded-xl p-4 text-center transition-all', item.highlight ? 'bg-primary/5 border border-primary/20 ring-1 ring-primary/10' : 'bg-muted/30 border border-transparent')}>
              <div className='flex items-center justify-center gap-2 mb-2'>
                <item.icon className={cn('size-4', item.highlight ? 'text-primary' : 'text-muted-foreground')} />
                <span className={cn('text-xs font-medium', item.highlight ? 'text-primary' : 'text-muted-foreground')}>{item.label}</span>
              </div>
              <div className={cn('text-3xl font-bold tabular-nums', item.highlight ? 'text-primary' : 'text-foreground')}>{item.value}</div>
            </div>
          ))}
        </div>

        {/* Total Summary */}
        <div className='flex items-center justify-between py-3 px-4 rounded-lg bg-muted/20 border border-border/50'>
          <span className='text-sm font-medium text-foreground'>{t('duties:activityReport.total')}</span>
          <span className='text-sm text-muted-foreground'>{t('duties:activityReport.totalDeployments', { count: totalDeployments })}</span>
        </div>

        {/* Notfallscheinbeteiligung Calculation */}
        {hasNotfallscheinbeteiligung && notfallscheinbeteiligungRate && (
          <div className='rounded-xl bg-gradient-to-br from-primary/5 via-primary/3 to-transparent border border-primary/20 p-4'>
            <div className='flex items-start gap-3'>
              <div className='flex items-center justify-center size-9 rounded-lg bg-primary/10 shrink-0'>
                <Euro className='size-4 text-primary' />
              </div>
              <div className='flex-1 min-w-0'>
                <div className='text-sm font-semibold text-foreground mb-1'>Notfallscheinbeteiligung</div>
                <div className='flex items-baseline gap-2 flex-wrap'>
                  <span className='text-sm text-muted-foreground'>
                    {activityReport.kassenpatienten} Patienten × {fmt.format(notfallscheinbeteiligungRate)}
                  </span>
                  <span className='text-sm text-muted-foreground'>=</span>
                  <span className='text-lg font-bold text-primary'>{fmt.format(scheinbeteiligungTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
